import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useParams, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import InputField from '../../components/inputFields/InputField';
import Button from '../../components/buttons/Button';
import CustomLoading from '../../components/customLoading/CustomLoading';
import { createBlogTag, updateBlogTag, getBlogTag } from '../../services/blogTag';

const BlogTagFormPage = ({ onSuccess, onCancel, hideHeader = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({
        name: '',
        color: '#10B981',
        order: 0,
        is_active: true
    });

    const validationSchema = Yup.object({
        name: Yup.string()
            .required('Tag name is required')
            .min(2, 'Tag name must be at least 2 characters')
            .max(255, 'Tag name must be less than 255 characters'),
        color: Yup.string()
            .matches(/^#[0-9A-F]{6}$/i, 'Color must be a valid hex color code'),
        order: Yup.number()
            .min(0, 'Order must be a positive number')
            .integer('Order must be a whole number'),
        is_active: Yup.boolean()
    });


    useEffect(() => {
        if (isEdit && id) {
            fetchTagData();
        }
    }, [isEdit, id]);

    const fetchTagData = async () => {
        try {
            setInitialLoading(true);
            const response = await getBlogTag(id);
            if (response.status === 200) {
                const data = response.data;
                setInitialValues({
                    name: data.name || '',
                    color: data.color || '#10B981',
                    order: data.order || 0,
                    is_active: data.is_active !== undefined ? data.is_active : true
                });
            }
        } catch (error) {
            console.error('Error fetching tag:', error);
            toast.error('Failed to fetch tag data');
            if (onCancel) {
                onCancel();
            } else {
                navigate('/blog/tags');
            }
        } finally {
            setInitialLoading(false);
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setLoading(true);

            let response;
            if (isEdit) {
                response = await updateBlogTag({
                    id: id,
                    data: values
                });
            } else {
                response = await createBlogTag(values);
            }

            if (response.status === 200 || response.status === 201) {
                toast.success(`Tag ${isEdit ? 'updated' : 'created'} successfully`);
                if (onSuccess) {
                    onSuccess(response.data);
                } else {
                    navigate('/blog/tags');
                }
            } else {
                toast.error(`Failed to ${isEdit ? 'update' : 'create'} tag`);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error(`Failed to ${isEdit ? 'update' : 'create'} tag`);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const handleCancelClick = () => {
        if (onCancel) {
            onCancel();
        } else {
            navigate('/blog/tags');
        }
    };

    if (initialLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <CustomLoading size={100} />
            </div>
        );
    }

    return (
        <>
            {!hideHeader && (
                <>
                    <Breadcrumbs items={[
                        { name: 'Home', link: '/dashboard' },
                        { name: 'Blog', link: '/blog/dashboard' },
                        { name: 'Tags', link: '/blog/tags' },
                        { name: isEdit ? 'Edit Tag' : 'Create Tag' },
                    ]} />

                    <div className="py-3">
                        <div className="mb-6">
                            <StyledHeadingText text={isEdit ? 'Edit Tag' : 'Create New Tag'} />
                            <p className="text-sm text-gray-600 mt-1">
                                {isEdit ? 'Update tag information' : 'Add a new blog tag'}
                            </p>
                        </div>
                    </div>
                </>
            )}

            <div className={`${!hideHeader ? 'bg-white rounded-lg shadow-sm border border-gray-200 p-6' : ''}`}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                    key={isEdit ? `edit-${id}` : 'create'}
                >
                    {({ values, setFieldValue, isSubmitting }) => (
                        <Form className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <InputField
                                        name="name"
                                        label="Tag Name"
                                        placeholder="Enter tag name"
                                        required
                                    />
                                </div>
                                <div>
                                    <InputField
                                        name="color"
                                        label="Color"
                                        placeholder="#10B981"
                                        type="color"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <InputField
                                        name="order"
                                        label="Order"
                                        type="number"
                                        placeholder="0"
                                        min="0"
                                    />
                                </div>
                                <div className="flex items-center">
                                    <InputField
                                        name="is_active"
                                        label="Active"
                                        type="checkbox"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <Button
                                    type="button"
                                    onClick={handleCancelClick}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting || loading}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : (isEdit ? 'Update Tag' : 'Create Tag')}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default BlogTagFormPage;
